import { Spacer, VDBText } from "@components/atoms";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { getBoxShadow } from "@utils/product/productValidators";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import OptionHeader from "../OptionHeader";
import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { JewelryBuilderSliderProps } from "./types";
import { useEffect, useRef, useState } from "react";
import { useJewelryConfiguratorState } from "@scenes/configurator/JewelryConfigurator/Context/JewelryConfiguratorContext";
import ResetButtonComponent from "../ResetButton";
import { isKeyPresent } from "@scenes/configurator/JewelryConfigurator/utils";
import JewelryBuilderErrorMessage from "../JewelryBuilderErrorMessage";
import { Slider } from "@miblanchard/react-native-slider";
import React from "react";
import { DefaultTheme } from "styled-components/native";

const JewelryBuilderSlider = ({
  configObject,
  productType,
  fromReviewPage,
  isOpen,
  onToggle,
  fromOrderCart,
  fromRequest,
  enterSecondFlow = false,
  onSelect,
}: JewelryBuilderSliderProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const [sliderValue, setSliderValue] = useState(0);
  const {
    updateJewelryConfigInfo,
    jcSelectedOption,
    addToCartPressed,
    fetchValidationMessage,
    validateRequiredOptions,
  } = useJewelryConfiguratorState();

  const initialCenterSizeRef = useRef<string>("");
  const isInitializedRef = useRef(false);
  const lastCommittedIndexRef = useRef<number>(0);
  if (configObject?.option_value && configObject?.option_value.length > 0) {
    configObject.option_value = configObject.option_value.filter(
      (item) => item.enabled
    );
  }

  useEffect(() => {}, [addToCartPressed]);

  useEffect(() => {
    const index = configObject.option_value.findIndex((option) =>
      configObject?.selected_item
        ? option.value === configObject?.selected_item
        : option.value ===
          jcSelectedOption.current.option_value[configObject.option_type]
    );
    const resolvedIndex = index !== -1 ? index : 0;
    setSliderValue(resolvedIndex);
    const resolvedValue =
      configObject.option_value[resolvedIndex]?.slug ||
      configObject.option_value[resolvedIndex]?.value;
    // Only capture the initial value once. After the user commits a new value,
    // updateJewelryConfigInfo → setItemDetail → setAllTypes updates configObject
    // which re-fires this effect. Without this guard, initialCenterSizeRef would
    // be overwritten with the new value, breaking the "revert to initial" detection.
    if (!isInitializedRef.current) {
      initialCenterSizeRef.current = resolvedValue ?? "";
      lastCommittedIndexRef.current = resolvedIndex;
      isInitializedRef.current = true;
    }
    updateJewelryConfigInfo(configObject?.option_type, [
      configObject,
      resolvedValue,
    ]);
  }, [configObject?.selected_item]);

  const handleSliderSelect = (newIndex: number) => {
    const isCenterSize = configObject?.option_type === "center_size";
    const newValue =
      configObject.option_value[newIndex]?.slug ||
      configObject.option_value[newIndex]?.value;

    if (enterSecondFlow && isCenterSize) {
      setSliderValue(newIndex);
      const previousIndex = lastCommittedIndexRef.current;
      const commitFn = () => {
        updateJewelryConfigInfo(configObject?.option_type, [
          configObject,
          newValue,
        ]);
        if (
          isKeyPresent(
            configObject.option_type,
            jcSelectedOption.current.option_error
          )
        ) {
          validateRequiredOptions();
        }
        lastCommittedIndexRef.current = newIndex;
      };
      const revertFn = () => {
        setSliderValue(previousIndex);
      };
      onSelect?.(
        initialCenterSizeRef.current,
        newValue ?? "",
        commitFn,
        revertFn
      );
      return;
    }

    setSliderValue(newIndex);
    updateJewelryConfigInfo(configObject?.option_type, [
      configObject,
      newValue,
    ]);
    if (
      isKeyPresent(
        configObject.option_type,
        jcSelectedOption.current.option_error
      )
    ) {
      validateRequiredOptions();
    }
    if (isCenterSize) {
      onSelect?.(initialCenterSizeRef.current, newValue ?? "");
    }
  };

  const CustomMark = () => {
    return (
      <SliderValue>
        <VDBText
          color={theme["core-05"]}
          type={typography["Body-M"]}
          textAlign="center"
        >
          {configObject.option_value[sliderValue]?.label}
        </VDBText>
      </SliderValue>
    );
  };

  return (
    <ContainerView isOpen={isOpen}>
      <OptionHeader
        title={configObject.option_name}
        value={configObject.option_value[sliderValue]?.label}
        helperText={configObject.option_helper_text}
        productType={productType}
        fromReviewPage={fromReviewPage}
        isOpen={isOpen}
        onToggle={onToggle}
        fromOrderCart={fromOrderCart}
        fromRequest={fromRequest}
        disableToggleButton={configObject?.disable_modify_variation ?? false}
      />
      {(isOpen || !fromReviewPage) && (
        <>
          <Spacer size={SpaceType.Extra} />
          <Overlay>
            <LeftSliderButton
              onPress={() => {
                if (sliderValue > 0) {
                  handleSliderSelect(sliderValue - 1);
                }
              }}
            >
              <VDBIcon
                color={theme["core-05"]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.MINUS_CIRCLE}
              />
            </LeftSliderButton>
            <SliderContainer>
              <Slider
                animateTransitions
                renderAboveThumbComponent={CustomMark}
                renderThumbComponent={() => {
                  return (
                    <SliderMarker>
                      <InnerCircle />
                    </SliderMarker>
                  );
                }}
                onValueChange={(value) => {
                  handleSliderSelect(value?.[0]);
                }}
                value={sliderValue}
                minimumValue={0}
                maximumValue={configObject.option_value.length - 1}
                step={1}
                trackClickable={true}
                trackStyle={{ height: 6 }}
                maximumTrackTintColor={theme["option-bg-hover"]}
                minimumTrackTintColor={theme["toggle-bg-on"]}
              />
            </SliderContainer>
            <RightSliderButton
              onPress={() => {
                if (sliderValue < configObject.option_value.length - 1) {
                  handleSliderSelect(sliderValue + 1);
                }
              }}
            >
              <VDBIcon
                color={theme["core-05"]}
                size={24}
                variant={IconsVariant.VDBCustom}
                name={VDBIcons.ADD_CIRCLE}
              />
            </RightSliderButton>
          </Overlay>
          <BottomContainer
            showError={isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            )}
          >
            {isKeyPresent(
              configObject.option_type,
              jcSelectedOption.current.option_error
            ) && (
              <JewelryBuilderErrorMessage
                message={fetchValidationMessage(
                  configObject.option_name,
                  false
                )}
              />
            )}
            {configObject?.reset_button_visible && (
              <ResetButtonComponent productType={productType} />
            )}
          </BottomContainer>
        </>
      )}
    </ContainerView>
  );
};

const ContainerView = styled.View<{
  isOpen?: boolean;
}>`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: 8px;
  padding-horizontal: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${({ isOpen }: { isOpen: boolean }) =>
    isOpen ? "0px" : `${SpaceType.Extra}px`};
`;

const Overlay = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 64px;
`;

const SliderContainer = styled.View`
  flex: 1;
`;

const SliderMarker = styled.View`
  box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
    getBoxShadow(theme.Shadows.Gentle)};
  align-items: center;
  justify-content: center;
  outline-style: none;
  border-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["toggle-bg-static"]};
  border-width: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["toggle-cont"]};
  height: 24px;
  width: 24px;
  border-radius: 15px;
`;

const SliderValue = styled.View`
  align-items: center;
  justify-content: center;
  background-color: "red";
  height: 20px;
  left: -25px;
  width: 50px;
`;

const InnerCircle = styled.View`
  height: 8px;
  width: 8px;
  border-radius: 20px;
  border-color: ${({ theme }: { theme: DefaultTheme }) =>
    theme["option-bg-static"]};
  border-width: 5px;
  align-items: center;
  box-shadow: ${({ theme }: { theme: DefaultTheme }) =>
    getBoxShadow(theme.Shadows.Gentle)};
`;

const LeftSliderButton = styled.Pressable``;
const RightSliderButton = styled.Pressable``;

const BottomContainer = styled.View<{
  showError: boolean;
}>`
  flex-direction: row;
  justify-content: ${({ showError }: { showError: boolean }) =>
    showError ? "space-between" : "flex-end"};
  margin-top: 8px;
`;

export default JewelryBuilderSlider;
